import React from 'react';
import { Link } from 'react-router-dom';
import { formatPlayerName } from '../../utils/playerName';

const getPlayerRating = (player) => {
    const rating = Number(player?.stats?.rating ?? player?.live_rating ?? player?.liveRating);
    return Number.isFinite(rating) ? rating : null;
};

/**
 * Picks the highest rated player across both teams from the final player
 * statistics the page already receives. Returns null when the engine never
 * sent ratings, so the card can be skipped entirely.
 */
export const derivePlayerOfTheMatch = (homeTeam, awayTeam) => {
    const candidates = [
        ...(Array.isArray(homeTeam?.players) ? homeTeam.players : []).map((player) => ({ player, side: 'home' })),
        ...(Array.isArray(awayTeam?.players) ? awayTeam.players : []).map((player) => ({ player, side: 'away' }))
    ].filter((entry) => getPlayerRating(entry.player) !== null);

    if (candidates.length === 0) return null;

    return candidates.reduce((best, entry) => (
        getPlayerRating(entry.player) > getPlayerRating(best.player) ? entry : best
    ), candidates[0]);
};

const buildPlayerStatLine = (player) => {
    const stats = player?.stats || {};
    const parts = [];
    const goals = Number(stats.goals);
    const assists = Number(stats.assists);
    const shots = Number(stats.shots);
    const saves = Number(stats.saves);

    if (Number.isFinite(goals) && goals > 0) parts.push(`${goals} ${goals === 1 ? 'goal' : 'goals'}`);
    if (Number.isFinite(assists) && assists > 0) parts.push(`${assists} ${assists === 1 ? 'assist' : 'assists'}`);
    if (Number.isFinite(shots) && shots > 0) parts.push(`${shots} ${shots === 1 ? 'shot' : 'shots'}`);
    if (Number.isFinite(saves) && saves > 0) parts.push(`${saves} ${saves === 1 ? 'save' : 'saves'}`);

    return parts.join(' | ');
};

const getInitials = (name) => String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/**
 * Full-time state: hero score, player of the match when ratings are available,
 * and the route onwards to the existing match summary page.
 */
function MatchFullTimePanel({
    homeScore,
    awayScore,
    resultText,
    playerOfTheMatch,
    summaryHref,
    label = 'FULL-TIME'
}) {
    const potmRating = playerOfTheMatch ? getPlayerRating(playerOfTheMatch.player) : null;
    const potmStatLine = playerOfTheMatch ? buildPlayerStatLine(playerOfTheMatch.player) : '';
    const potmName = playerOfTheMatch
        ? formatPlayerName(playerOfTheMatch.player?.shortName || playerOfTheMatch.player?.name || 'Unknown')
        : '';

    return (
        <>
            <div className="mx-card mx-ft-hero" data-testid="match-fulltime-hero">
                <div className="mx-ft-hero__label">{label}</div>
                <div className="mx-ft-hero__score">
                    {homeScore}
                    <span className="mx-score__dash"> - </span>
                    {awayScore}
                </div>
                {resultText && <div className="mx-ft-hero__sub">{resultText}</div>}
            </div>

            {playerOfTheMatch && potmRating !== null && (
                <div className="mx-card" data-testid="match-potm-card">
                    <h5>Player of the match</h5>
                    <div className="mx-potm">
                        <span className="mx-potm__avatar">
                            {getInitials(potmName)}
                        </span>
                        <span className="mx-potm__main">
                            <span className="mx-potm__name">
                                {potmName}
                            </span>
                            {potmStatLine && <span className="mx-potm__stat">{potmStatLine}</span>}
                        </span>
                        <span className="mx-potm__rating">{potmRating.toFixed(1)}</span>
                    </div>
                </div>
            )}

            {summaryHref && (
                <div className="mx-cta-row">
                    <Link className="mx-cta mx-cta--primary" to={summaryHref}>MATCH SUMMARY</Link>
                </div>
            )}
        </>
    );
}

export default MatchFullTimePanel;
